"use client";

import Image from "next/image";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

const IMAGE_BASE = "/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/ProductionServices";

const SERVICES = [
	{
		keyPrefix: "productionService1",
		anchor: "cnc-machining",
		image: `${IMAGE_BASE}/cnc-machining-parts.jpg`,
		href: "/cnc-machining",
		imageAspectClassName: "aspect-square",
		rows: 5,
	},
	{
		keyPrefix: "productionService2",
		anchor: "plastic-injection-molding",
		image: `${IMAGE_BASE}/plastic-injection molding-parts.jpg`,
		href: "/plastic-injection-molding",
		imageAspectClassName: "aspect-[4/3.6]",
		rows: 4,
	},
	{
		keyPrefix: "productionService3",
		anchor: "sheet-metal-fabrication",
		image: `${IMAGE_BASE}/sheet-metal-fabrication-parts.jpg`,
		href: "/sheet-metal-fabrication",
		imageAspectClassName: "aspect-[4/4.5]",
		rows: 4,
	},
	{
		keyPrefix: "productionService4",
		anchor: "3d-printing",
		image: `${IMAGE_BASE}/3d-printing-parts.jpg`,
		href: "/3d-printing",
		imageAspectClassName: "aspect-[4/4.5]",
		rows: 5,
	},
	{
		keyPrefix: "productionService5",
		anchor: "vacuum-casting",
		image: `${IMAGE_BASE}/vacuum-casting-parts.jpg`,
		href: "/vacuum-casting",
		imageAspectClassName: "aspect-square",
		rows: 4,
	},
	{
		keyPrefix: "productionService6",
		anchor: "die-casting",
		image: `${IMAGE_BASE}/die-casting-parts.jpg`,
		href: "/die-casting",
		imageAspectClassName: "aspect-square",
		rows: 4,
	},
	{
		keyPrefix: "productionService7",
		anchor: "extrusion",
		image: `${IMAGE_BASE}/extrusion-parts.jpg`,
		href: "/aluminum-extrusion",
		imageAspectClassName: "aspect-[4/4.5]",
		rows: 4,
	},
	{
		keyPrefix: "productionService8",
		anchor: "spring-manufacturing",
		image: `${IMAGE_BASE}/spring-manufacturing-parts.jpg`,
		href: "/spring-manufacturing",
		imageAspectClassName: "aspect-[4/4.5]",
		rows: 6,
	},
	{
		keyPrefix: "productionService9",
		anchor: "gear-manufacturing",
		image: `${IMAGE_BASE}/gear-manufacturing-parts.jpg`,
		href: "/gear-machining",
		imageAspectClassName: "aspect-[4/5]",
		rows: 7,
	},
] as const;

const getRowKeys = (count: number) => Array.from({ length: count }, (_, index) => `row${index + 1}`);

export default function OnDemandProductionServices() {
	const t = useTranslations("new-on-demand-manufacturing");
	const { openModalInquiry } = useModalInquiryContext();

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="inline-block text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("productionServicesTitleLine1")}</span>
						<span className="mt-1 flex items-end gap-4">
							<span>{t("productionServicesTitleLine2")}</span>
							<span className="mb-1 h-[3px] w-14 bg-themePrimary400 sm:w-16 md:w-20" aria-hidden="true" />
						</span>
					</h2>
				</header>

				<div className="mb-8 flex flex-wrap gap-3">
					{SERVICES.map((service) => (
						<a
							key={service.keyPrefix}
							href={`#${service.anchor}`}
							className="rounded border border-themePrimary400/50 bg-white px-5 py-2 text-sm font-medium text-themePrimary400 transition-colors hover:border-themePrimary400 hover:bg-themePrimary400 hover:text-white"
						>
							{t(`${service.keyPrefix}Title`)}
						</a>
					))}
				</div>

				<div className="space-y-10 sm:space-y-12 lg:space-y-16">
					{SERVICES.map((service, index) => {
						const isImageFirst = index % 2 === 1;

						return (
							<div
								key={service.keyPrefix}
								id={service.anchor}
								className="grid scroll-mt-24 items-center gap-8 lg:grid-cols-2 lg:gap-16"
							>
								<div className={isImageFirst ? "lg:order-2" : undefined}>
									<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">
										{t(`${service.keyPrefix}Title`)}
									</h3>
									<p className="mb-5 text-sm leading-relaxed text-black md:text-base">
										{t(`${service.keyPrefix}Desc`)}
									</p>

									<div className="overflow-x-auto">
										<table className="w-full min-w-[520px] border-collapse">
											<thead>
												<tr className="border-b border-[#d1d1d1]">
													<th className="w-[42%] py-3 pr-4 text-left text-sm font-bold text-black md:text-base">
														{t(`${service.keyPrefix}Col1`)}
													</th>
													<th className="w-[58%] py-3 pl-4 text-left text-sm font-bold text-black md:text-base">
														{t(`${service.keyPrefix}Col2`)}
													</th>
												</tr>
											</thead>
											<tbody className="border-b border-[#d1d1d1]">
												{getRowKeys(service.rows).map((rowKey) => (
													<tr key={rowKey} className="border-b border-[#d1d1d1] last:border-b-0">
														<td className="py-4 pr-4 align-top text-sm text-black md:text-base">
															{t(`${service.keyPrefix}${rowKey}Label`)}
														</td>
														<td className="py-4 pl-4 align-top text-sm leading-relaxed text-black md:text-base">
															{t(`${service.keyPrefix}${rowKey}Value`)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>

									<div className="mt-6 flex flex-wrap items-center gap-4">
										<button
											type="button"
											onClick={openModalInquiry}
											className="inline-flex items-center justify-center rounded bg-themePrimary400 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] md:px-6 md:py-3"
										>
											{t("productionServicesGetInstantQuote")}
										</button>
										<Link
											href={service.href}
											className="inline-flex items-center gap-1 text-sm font-semibold text-themePrimary400 transition-colors hover:text-[#83b751]"
										>
											{t("productionServicesLearnMore")}
											<ArrowUpRight className="h-4 w-4" weight="bold" aria-hidden="true" />
										</Link>
									</div>
								</div>

								<div className={isImageFirst ? "lg:order-1" : undefined}>
									<div
										className={`relative w-full border border-[#d1d1d1] bg-white ${service.imageAspectClassName}`}
									>
										<Image
											src={service.image}
											alt={t(`${service.keyPrefix}ImageAlt`)}
											fill
											className="object-contain p-6"
											sizes="(max-width: 1024px) 100vw, 50vw"
										/>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
